export default {
    scoreOutOfFive: '{{score}} / 5',
    header: {
        title: 'Compensation',
        subtitle: 'Plan and distribute your salary increase envelopes.',
        cycleLabel: 'Feedback cycle',
        cyclePlaceholder: 'Select a cycle',
    },
    tabs: {
        navLabel: 'Envelopes',
        newEnvelope: 'New envelope',
        newEnvelopeName: 'Envelope {{n}}',
        owner: '{{managerLabel}}: {{name}}',
        unknownOwner: '{{managerLabel}}: unknown',
        totalEnvelopesLabel: 'Total envelopes:',
    },
    cycleBudget: {
        label: 'Cycle budget',
        placeholder: 'Optional',
        overrun: 'The envelopes total exceeds the cycle budget by {{amount}}.',
    },
    status: {
        draft: 'Draft',
        closed: 'Closed',
    },
    actionBar: {
        history: 'History',
        close: 'Close envelope',
        reopen: 'Reopen envelope',
        closedNotice: 'This envelope is closed: it can be read, not changed.',
    },
    audience: {
        title: '1. Envelope audience',
        nameLabel: 'Envelope name',
        namePlaceholder: 'Envelope name',
        deleteEnvelope: 'Delete envelope',
        all: 'Entire team',
        allDescription: "Include every member of the {{managerLabel}}'s team.",
        allCompany: 'Entire company',
        allCompanyDescription: 'Include every employee in the organization.',
        specific: 'Specific teams',
        specificDescription: 'Target employees based on selected attributes.',
        includedCount: '{{count}} employee included in this envelope.',
        includedCount_one: '{{count}} employee included in this envelope.',
        includedCount_other: '{{count}} employees included in this envelope.',
        withoutEvaluationCount: '{{count}} employee without an evaluation.',
        withoutEvaluationCount_one: '{{count}} employee without an evaluation.',
        withoutEvaluationCount_other: '{{count}} employees without an evaluation.',
        withoutSalaryCount: '{{count}} employee without a known salary.',
        withoutSalaryCount_one: '{{count}} employee without a known salary.',
        withoutSalaryCount_other: '{{count}} employees without a known salary.',
        noCriterion:
            'Choose at least one attribute and one value: until then, the envelope covers nobody.',
        noCriterionReadOnly: 'No criterion chosen: the envelope covers nobody.',
        overlapWarning:
            'Some employees also belong to another envelope in this cycle: they could receive two increases.',
    },
    budgetCard: {
        title: '2. Envelope budget',
        modeLabel: 'Calculation mode',
        modeAmount: 'Fixed amount ($)',
        modePercent: '% of payroll',
        amountLabel: 'Envelope amount ($)',
        percentLabel: 'Share of payroll (%)',
        payrollLabel: 'Payroll',
        headcountSummary: '{{count}} person · {{ratio}} average',
        headcountSummary_one: '{{count}} person · {{ratio}} average',
        headcountSummary_other: '{{count}} people · {{ratio}} average',
        importSalaries: 'Import salaries',
        percentHelp:
            'The percentage is converted into an amount right away, and only the amount is kept: if the payroll changes, the budget stays and the percentage moves.',
    },
    strategyCard: {
        modeLevel: 'By level',
        modeScore: 'By score',
        modeLevelDescription:
            'Everyone in the same level receives the same percentage. The total adjusts itself automatically to stay within budget.',
        modeScoreDescription:
            'Every tenth of a point counts, so two people who exceed expectations do not receive the same amount. The total adjusts itself automatically to stay within budget.',
        level: {
            headcount: '{{count}} person',
            headcount_one: '{{count}} person',
            headcount_other: '{{count}} people',
            shareOfSalary: 'or {{ratio}} of salary',
            timesAverage: '× the average',
        },
        score: {
            thresholdLabel: 'Minimum score to be eligible',
            thresholdHelp:
                'Below this score, no increase is granted. The whole budget goes to the people who reach it.',
            thresholdResult: '{{count}} person is excluded with this threshold.',
            thresholdResult_one: '{{count}} person is excluded with this threshold.',
            thresholdResult_other: '{{count}} people are excluded with this threshold.',
            spreadLabel: 'Gap between high and low performance',
            spreadHelp:
                'The further right the slider, the more the best performers receive a larger share of the budget. To the left, the increases move closer together.',
            spreadResult:
                'In practice: a score of 4.5 receives about {{ratio}} times the increase of a score of 3.0.',
            spreadResultNa:
                'In practice: a score of 3.0 is not eligible with the current threshold.',
        },
        summaryTable: {
            caption: 'Number of people and total increase amount, for each performance level.',
            levelColumn: 'Performance level',
            headcountRow: 'Number of people',
            amountRow: 'Total increase amount',
        },
    },
    adjustments: {
        title: '3. Individual adjustments',
        subtitle: 'Adjust an increase: the envelope balance is redistributed automatically.',
        allocated: 'Allocated',
        overrun: 'The envelope is over budget by {{amount}}.',
        residual: 'Rounding residual of {{amount}} carried by {{name}}.',
        toolbar: {
            searchPlaceholder: 'Search for a person…',
            exportSalaries: 'Export salary list',
        },
        columns: {
            person: 'Person',
            performance: 'Performance',
            salary: 'Current salary',
            proposed: 'Proposed',
            increase: 'Increase',
            newSalary: 'New salary',
            note: 'Note',
        },
        notePlaceholder: 'Add a note',
        calibratedBadge: 'calibrated',
        exceedsCap: 'Above the {{max}} individual ceiling.',
        lock: {
            lock: 'Lock this row',
            unlock: 'Unlock and return to automatic calculation',
        },
    },
    saveBar: {
        save: 'Save',
        discard: 'Discard',
        saving: 'Saving…',
        savedMessage: 'Changes saved.',
    },
    history: {
        title: 'Change history',
        empty: 'No changes recorded yet.',
        version: 'Version {{number}} · {{origin}}',
        unknownAuthor: 'Unknown author',
        restore: 'Restore',
        restoreTitle: 'Restore this version',
        restoreMessage:
            'The envelope returns to version {{number}}. The change is itself recorded as a new version.',
        restoreSuccess: 'Version restored.',
        restoreFailed: 'Could not restore this version.',
        cancel: 'Cancel',
        origins: {
            save: 'Save',
            restore: 'Restore',
            close: 'Close',
            reopen: 'Reopen',
        },
        audienceAll: 'Entire team',
        audienceAllCompany: 'Entire company',
        audienceSpecific: 'Specific teams',
        values: {
            locked: 'locked',
            unlocked: 'unlocked',
        },
        changes: {
            name: 'Envelope name',
            audienceType: 'Audience',
            audienceFilters: 'Audience filters',
            budget: 'Budget',
            budgetInputMode: 'Budget mode',
            status: 'Status',
            distributionMode: 'Distribution mode',
            scoreThreshold: 'Minimum score',
            scoreSpread: 'Performance gap',
            levelMultiplier: 'Multiplier · {{label}}',
            lineSalary: '{{name}}’s salary',
            linePercent: '{{name}}’s increase',
            lineLock: '{{name}}’s lock',
            lineNote: '{{name}}’s note',
        },
    },
    import: {
        title: 'Import salaries',
        description:
            'Drop a CSV file or paste your data: one line per person, the email, the name, then the annual salary.',
        dropPrompt: 'Drag and drop a CSV file here, or',
        dropBrowse: 'browse your folders',
        dropHere: 'Drop your file here',
        analyzing: 'Analyzing…',
        dropRetry: 'Click or drop another file to try again',
        invalidFileType: 'This file is not a CSV.',
        fileRejected: 'File rejected: {{name}}',
        fileSelected: 'File selected: {{name}}',
        chooseFile: 'Choose a CSV file',
        pastePlaceholder: 'camille.tremblay@example.ca;Camille Tremblay;64000',
        downloadTemplate: 'Download the template',
        templateFileName: 'salary-template',
        templateColumns: 'Template columns: email, name, salary.',
        analyze: 'Analyse',
        matched: '{{count}} person recognized',
        matched_one: '{{count}} person recognized',
        matched_other: '{{count}} people recognized',
        matchedMore: '+ {{count}} more',
        matchedMore_one: '+ {{count}} more',
        matchedMore_other: '+ {{count}} more',
        unmatched: 'Not recognized: {{names}}',
        unmatched_one: 'Not recognized: {{names}}',
        unmatched_other: 'Not recognized: {{names}}',
        duplicate: 'Duplicate: {{names}}',
        duplicate_one: 'Duplicate: {{names}}',
        duplicate_other: 'Duplicates: {{names}}',
        invalidRow: 'Line {{line}} is invalid.',
        reasons: {
            missingAmount: 'No amount could be read.',
            invalidAmount: 'The amount is not a positive number.',
            amountOutOfRange: 'The amount is above the salary the report accepts.',
            missingName: 'No name or email to match the amount to.',
            ambiguousName: 'That name matches several people of the audience.',
            ambiguousAmount:
                'A comma splits the amount, so it reads more than one way: write it without a thousands separator.',
        },
        cancel: 'Cancel',
        confirm: 'Import salaries',
        done: '{{count}} salary imported.',
        done_one: '{{count}} salary imported.',
        done_other: '{{count}} salaries imported.',
        error: 'Could not parse the imported file.',
    },
    delete: {
        title: 'Delete envelope',
        message: 'This envelope and every increase entered will be deleted.',
        lastEnvelopeWarning: 'It is the last envelope of this cycle: nothing will be left to plan.',
        confirm: 'Delete',
        cancel: 'Cancel',
        success: 'Envelope deleted.',
        error: 'Could not delete the envelope.',
    },
    empty: {
        noCycle: {
            title: 'No feedback cycle',
            message: 'Compensation planning starts from a feedback cycle; none exists yet.',
        },
        noEnvelope: {
            title: 'No envelope',
            message: 'Create an envelope for this feedback cycle.',
        },
        noAudience: {
            message: 'No employee in this envelope.',
        },
        noEvaluation: {
            message: 'No evaluation is available for this cycle.',
        },
    },
    errors: {
        versionConflict:
            'This envelope was changed by someone else. Reload it to see the latest changes.',
        reload: 'Reload',
        nameConflict: 'An envelope already has this name for this cycle.',
        envelopeClosed: 'This envelope is closed and can no longer be edited.',
        saveFailed: 'Could not save the change.',
        closeFailed: 'Could not close the envelope.',
        reopenFailed: 'Could not reopen the envelope.',
        cycleBudgetFailed: 'Could not save the cycle budget.',
        exportFailed: 'Could not export the salary list.',
        audienceOutsideTeam:
            'An audience criterion reaches outside the team of the {{managerLabel}} the envelope belongs to.',
        createFailed: 'Could not create the envelope.',
        previewFailed:
            'The figures could not be recalculated: they do not reflect your latest changes yet.',
        loadFailed: {
            title: 'Could not load compensation',
            message: 'The request did not go through. Nothing was changed: try again in a moment.',
            retry: 'Try again',
        },
    },
    export: {
        fileNameBase: 'salaries',
    },
    disabled: {
        title: 'Compensation not enabled',
        message: 'Compensation planning is not enabled for your organization.',
    },
};
